// src/pages/CreateJudgmentWizard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const MAX_PARTIES = 10;


function cleanStr(s) {
  return (s ?? "").toString().trim();
}

function normalizeDateToISO(input) {
  const s = cleanStr(input);
  if (!s) return null;

  // Already ISO-like: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // Accept: MM/DD/YYYY or M/D/YYYY
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const mm = String(m[1]).padStart(2, "0");
    const dd = String(m[2]).padStart(2, "0");
    const yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
  }

  // Unknown format
  return s;
}

function getTenant() {
  const host = window.location.hostname;

  // app.jggsystem.com -> tenant "app"
  if (host === "app.jggsystem.com") return "root";

  // <tenant>.app.jggsystem.com -> tenant "<tenant>"
  if (host.endsWith(".app.jggsystem.com")) return host.split(".")[0];

  // fallback
  return "app";
}

async function apiFetch(path, opts = {}) {
  const headers = {
    ...(opts.headers || {}),
        "x-tenant": getTenant(),
  };

  return fetch(path, {
    credentials: "include",
    ...opts,
    headers,
  });
}

function PartyEditor({ title, parties, setParties }) {
  function addParty() {
    if (parties.length >= MAX_PARTIES) return;
    setParties([
      ...parties,
      { name: "", email: "", phone: "", address: "", dob: "" },
    ]);
  }

  function removeParty(idx) {
    const copy = [...parties];
    copy.splice(idx, 1);
    setParties(copy);
  }

  function updateParty(idx, key, value) {
    const copy = [...parties];
    copy[idx] = { ...copy[idx], [key]: value };
    setParties(copy);
  }

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <button
          type="button"
          onClick={addParty}
          disabled={parties.length >= MAX_PARTIES}
          style={btn}
          title={parties.length >= MAX_PARTIES ? "Max 10" : "Add"}
        >
          + Add
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        {parties.length === 0 ? (
          <div style={{ color: "#666" }}>None added yet.</div>
        ) : (
          parties.map((p, idx) => (
            <div key={idx} style={{ ...subCard, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ fontWeight: 700 }}>#{idx + 1}</div>
                <button
                  type="button"
                  onClick={() => removeParty(idx)}
                  style={btnDanger}
                >
                  Remove
                </button>
              </div>

              <div style={grid2}>
                <label style={field}>
                  Name *
                  <input
                    value={p.name}
                    onChange={(e) => updateParty(idx, "name", e.target.value)}
                    style={input}
                    placeholder="Full name / business name"
                  />
                </label>

                <label style={field}>
                  Phone
                  <input
                    value={p.phone}
                    onChange={(e) => updateParty(idx, "phone", e.target.value)}
                    style={input}
                    placeholder="(###) ###-####"
                  />
                </label>

                <label style={field}>
                  Email
                  <input
                    value={p.email}
                    onChange={(e) => updateParty(idx, "email", e.target.value)}
                    style={input}
                    placeholder="name@email.com"
                  />
                </label>

                <label style={field}>
                  DOB (optional)
                  <input
                    value={p.dob}
                    onChange={(e) => updateParty(idx, "dob", e.target.value)}
                    style={input}
                    placeholder="YYYY-MM-DD"
                  />
                </label>

                <label style={{ ...field, gridColumn: "1 / -1" }}>
                  Address
                  <input
                    value={p.address}
                    onChange={(e) => updateParty(idx, "address", e.target.value)}
                    style={input}
                    placeholder="Street, City, State ZIP"
                  />
                </label>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ fontSize: 12, color: "#666" }}>
        Limit: {MAX_PARTIES} per side.
      </div>
    </div>
  );
}

// --- date safety helpers (STRICT) ---
function isISODateStrict(s) {
  if (typeof s !== "string") return false;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);

  if (month < 1 || month > 12) return false;

  // Days in month (handles leap years)
  const lastDay = new Date(year, month, 0).getDate();
  if (day < 1 || day > lastDay) return false;

  return true;
}

function isISODateTimeLoose(s) {
  // "2025-04-10T12:34" or "2025-04-10T12:34:56" (+ optional .ms + optional Z/offset)
  return (
    typeof s === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/.test(s.trim())
  );
}

function normalizeDateField(v) {
  if (v === undefined || v === null) return null;
  if (typeof v !== "string") return null;

  const s = v.trim();
  if (!s) return null;

  // Accept ISO date ONLY if it's a real calendar date
  if (isISODateStrict(s)) return s;

  // Optional: accept datetime if present
  if (isISODateTimeLoose(s)) return s;

  // Reject everything else (phone numbers, "2021-30-12", etc.)
  return null;
}


export default function CreateJudgmentWizard() {
  const navigate = useNavigate();

  const steps = useMemo(
    () => [
      "Client",
      "Case",
      "Court",
      "Amounts",
      "Parties",
      "Contact + Flags",
      "Review",
    ],
    []
  );

  const [step, setStep] = useState(0);

  // Remote data
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState("");

  // Form state (we create the judgment ONLY at the end)
  const [form, setForm] = useState({
    client_id: "",
    client_role: "",

    case_id: "",
    case_number: "",
    filing_date: "",
    incident_dates: "",

    court: "",
    county: "",
    court_address: "",
    court_city: "",
    court_state: "CA",

    claim_amount: "",
    judgment_date: "",
    principal: "",
    interest_rate: "0.10",
    interest_start_date: "",
    court_costs: "",
    total_judgment_entered: "",

    status: "OPEN",
    status_flags: "",
    follow_up_date: "",
    notes: "",

    letter_type_sent: "",
    payment_method: "",
    payment_updates: "",

    contact_first_name: "",
    contact_last_name: "",
    contact_phone: "",
    contact_address: "",
    contact_city: "",
    contact_state: "CA",
    contact_zip: "",
  });

  const [plaintiffs, setPlaintiffs] = useState([]);
  const [defendants, setDefendants] = useState([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveOk, setSaveOk] = useState("");

  useEffect(() => {
    let mounted = true;
    async function loadClients() {
      setClientsLoading(true);
      setClientsError("");
      try {
        const res = await apiFetch("/api/clients", { method: "GET" });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Clients load failed (${res.status}): ${txt}`);
        }
        const data = await res.json();
        if (mounted) setClients(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setClientsError(e?.message || "Failed to load clients");
      } finally {
        if (mounted) setClientsLoading(false);
      }
    }
    loadClients();
    return () => {
      mounted = false;
    };
  }, []);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function next() {
    setSaveError("");
    setSaveOk("");
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  }

  function prev() {
    setSaveError("");
    setSaveOk("");
    setStep((s) => Math.max(s - 1, 0));
  }

  function validateStep(s) {
    // keep validation light & practical (you can tighten later)
    if (s === 0) {
      if (!cleanStr(form.client_id)) return fail("Select a client.");
      // client_role is allowed to be blank (manual), but recommended
      return true;
    }
    if (s === 1) {
      if (!cleanStr(form.case_number)) return fail("Case number is required.");
      // case_id optional (but you want it)
      return true;
    }
    if (s === 2) {
      if (!cleanStr(form.court)) return fail("Court is required.");
      if (!cleanStr(form.county)) return fail("County is required.");
      return true;
    }
    if (s === 3) {
      if (!cleanStr(form.judgment_date)) return fail("Judgment date is required.");
      if (!cleanStr(form.principal)) return fail("Principal is required.");
      return true;
    }
    if (s === 4) {
      if (plaintiffs.length === 0) return fail("Add at least 1 plaintiff.");
      if (defendants.length === 0) return fail("Add at least 1 defendant.");
      const badP = plaintiffs.find((p) => !cleanStr(p.name));
      const badD = defendants.find((d) => !cleanStr(d.name));
      if (badP) return fail("Each plaintiff needs a name.");
      if (badD) return fail("Each defendant needs a name.");
      return true;
    }
    return true;
  }

  function fail(msg) {
    setSaveError(msg);
    window.scrollTo({ top: 0, behavior: "smooth" });
    return false;
  }

  function buildPayload() {
    // Only include optional fields when they have values
    const payload = {
      client_id: Number(form.client_id),
      client_role: cleanStr(form.client_role) || null,

      case_id: cleanStr(form.case_id) || null,
      case_number: cleanStr(form.case_number) || null,

      court: cleanStr(form.court) || null,
      county: cleanStr(form.county) || null,
      court_address: cleanStr(form.court_address) || null,
      court_city: cleanStr(form.court_city) || null,
      court_state: cleanStr(form.court_state) || null,

      filing_date: normalizeDateToISO(form.filing_date),
      incident_dates: normalizeDateToISO(form.incident_dates) || null,
      claim_amount: cleanStr(form.claim_amount) || null,

      judgment_date: normalizeDateToISO(form.judgment_date),
      principal: cleanStr(form.principal),
      interest_rate: cleanStr(form.interest_rate) || "0.10",
      interest_start_date: normalizeDateToISO(form.interest_start_date),
      status: cleanStr(form.status) || "OPEN",
      notes: cleanStr(form.notes) || null,

      status_flags: cleanStr(form.status_flags) || null,
      follow_up_date: normalizeDateToISO(form.follow_up_date),
      letter_type_sent: cleanStr(form.letter_type_sent) || null,
      payment_method: cleanStr(form.payment_method) || null,
      payment_updates: cleanStr(form.payment_updates) || null,
      total_judgment_entered: cleanStr(form.total_judgment_entered) || null,

      contact_first_name: cleanStr(form.contact_first_name) || null,
      contact_last_name: cleanStr(form.contact_last_name) || null,
      contact_phone: cleanStr(form.contact_phone) || null,
      contact_address: cleanStr(form.contact_address) || null,
      contact_city: cleanStr(form.contact_city) || null,
      contact_state: cleanStr(form.contact_state) || null,
      contact_zip: cleanStr(form.contact_zip) || null,

      court_costs: cleanStr(form.court_costs) || null,

      plaintiffs: plaintiffs.map((p) => ({
        name: cleanStr(p.name),
        email: cleanStr(p.email) || null,
        phone: cleanStr(p.phone) || null,
        address: cleanStr(p.address) || null,
        dob: normalizeDateToISO(p.dob),

      })),
      defendants: defendants.map((d) => ({
        name: cleanStr(d.name),
        email: cleanStr(d.email) || null,
        phone: cleanStr(d.phone) || null,
        address: cleanStr(d.address) || null,
        dob: normalizeDateToISO(d.dob),

      })),
    };

    return payload;
  }

  async function submit() {
    setSaveError("");
    setSaveOk("");

    // Validate all required steps before submit
    for (let s = 0; s < steps.length - 1; s++) {
      if (!validateStep(s)) {
        setStep(s);
        return;
      }
    }

    const payload = buildPayload();

    payload.follow_up_date = normalizeDateField(payload.follow_up_date);
    payload.judgment_date = normalizeDateField(payload.judgment_date);
    payload.filing_date = normalizeDateField(payload.filing_date);
    payload.interest_start_date = normalizeDateField(payload.interest_start_date);
    payload.incident_dates = normalizeDateField(payload.incident_dates);

    setSaving(true);
    try {
      const res = await apiFetch("/api/judgments/full", {
  method: "POST",
  headers: { "content-type": "application/json" },
  body: JSON.stringify(payload),
});
     

      const text = await res.text();
      if (!res.ok) {
        throw new Error(`Create failed (${res.status}): ${text}`);
      }

      const data = text ? JSON.parse(text) : null;
      console.log("CREATE /judgments/full response data:", data);
      setSaveOk("Judgment created successfully.");
      // Navigate to detail if route exists
      const newId =
        data?.judgment?.id ??
        data?.id ??
        data?.judgment_id ??
        data?.judgment?.judgment_id ??
        null;

      if (newId) {
        navigate(`/judgments/${newId}`);
      } else {
        navigate("/judgments");
      }
    } catch (e) {
      setSaveError(e?.message || "Create failed");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  }

  const pct = Math.round(((step + 1) / steps.length) * 100);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>New Judgment Wizard</h2>
          <div style={{ color: "#666", marginTop: 6 }}>
            Step {step + 1} of {steps.length}: <b>{steps[step]}</b>
          </div>
        </div>

        <div style={{ minWidth: 220 }}>
          <div style={{ fontSize: 12, color: "#666" }}>{pct}%</div>
          <div style={progressTrack}>
            <div style={{ ...progressFill, width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {(saveError || saveOk) && (
        <div style={{ ...notice, borderColor: saveError ? "#f5c2c7" : "#badbcc", background: saveError ? "#f8d7da" : "#d1e7dd" }}>
          {saveError ? <b>Error:</b> : <b>OK:</b>} {saveError || saveOk}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {step === 0 && (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>Client</h3>

            <label style={field}>
              Client *
              <select
                value={form.client_id}
                onChange={(e) => setField("client_id", e.target.value)}
                style={input}
                disabled={clientsLoading}
              >
                <option value="">-- Select client --</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name || c.contact_name || `Client #${c.id}`}
                  </option>
                ))}
              </select>
            </label>

            {clientsLoading && <div style={{ color: "#666" }}>Loading clients…</div>}
            {clientsError && <div style={{ color: "#b00020" }}>{clientsError}</div>}

            <label style={field}>
              Client role (manual)
              <input
                value={form.client_role}
                onChange={(e) => setField("client_role", e.target.value)}
                style={input}
                placeholder="ATTORNEY / CREDITOR / OTHER"
              />
            </label>

            <div style={{ fontSize: 12, color: "#666" }}>
              Client can be an attorney, creditor, or anything else — this is for reports/letters.
            </div>
          </div>
        )}

        {step === 1 && (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>Case</h3>

            <div style={grid2}>
              <label style={field}>
                Case ID (your internal ID)
                <input
                  value={form.case_id}
                  onChange={(e) => setField("case_id", e.target.value)}
                  style={input}
                  placeholder="JG-2025-1000"
                />
              </label>

              <label style={field}>
                Case number *
                <input
                  value={form.case_number}
                  onChange={(e) => setField("case_number", e.target.value)}
                  style={input}
                  placeholder="24SC000123"
                />
              </label>

              <label style={field}>
                Filing date
                <input
                  value={form.filing_date}
                  onChange={(e) => setField("filing_date", e.target.value)}
                  style={input}
                  placeholder="YYYY-MM-DD"
                />
              </label>

              <label style={field}>
                Incident dates
                <input
                  value={form.incident_dates}
                  onChange={(e) => setField("incident_dates", e.target.value)}
                  style={input}
                  placeholder="2024-10-05 (or a range / notes)"
                />
              </label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>Court</h3>

            <div style={grid2}>
              <label style={field}>
                Court *
                <input
                  value={form.court}
                  onChange={(e) => setField("court", e.target.value)}
                  style={input}
                  placeholder="Ventura Superior Court"
                />
              </label>

              <label style={field}>
                County *
                <input
                  value={form.county}
                  onChange={(e) => setField("county", e.target.value)}
                  style={input}
                  placeholder="Ventura"
                />
              </label>

              <label style={field}>
                Court city
                <input
                  value={form.court_city}
                  onChange={(e) => setField("court_city", e.target.value)}
                  style={input}
                  placeholder="Ventura"
                />
              </label>

              <label style={field}>
                Court state
                <input
                  value={form.court_state}
                  onChange={(e) => setField("court_state", e.target.value)}
                  style={input}
                  placeholder="CA"
                />
              </label>

              <label style={{ ...field, gridColumn: "1 / -1" }}>
                Court address
                <input
                  value={form.court_address}
                  onChange={(e) => setField("court_address", e.target.value)}
                  style={input}
                  placeholder="Street address"
                />
              </label>
            </div>
          </div>
        )}

        {step === 3 && (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>Amounts</h3>

            <div style={grid2}>
              <label style={field}>
                Claim amount
                <input
                  value={form.claim_amount}
                  onChange={(e) => setField("claim_amount", e.target.value)}
                  style={input}
                  placeholder="2500.00"
                />
              </label>

              <label style={field}>
                Judgment date *
                <input
                  value={form.judgment_date}
                  onChange={(e) => setField("judgment_date", e.target.value)}
                  style={input}
                  placeholder="YYYY-MM-DD"
                />
              </label>

              <label style={field}>
                Principal *
                <input
                  value={form.principal}
                  onChange={(e) => setField("principal", e.target.value)}
                  style={input}
                  placeholder="2500.00"
                />
              </label>

              <label style={field}>
                Court costs (optional → COST tx)
                <input
                  value={form.court_costs}
                  onChange={(e) => setField("court_costs", e.target.value)}
                  style={input}
                  placeholder="85.00"
                />
              </label>

              <label style={field}>
                Interest rate
                <input
                  value={form.interest_rate}
                  onChange={(e) => setField("interest_rate", e.target.value)}
                  style={input}
                  placeholder="0.10"
                />
              </label>

              <label style={field}>
                Interest start date (optional)
                <input
                  value={form.interest_start_date}
                  onChange={(e) => setField("interest_start_date", e.target.value)}
                  style={input}
                  placeholder="YYYY-MM-DD"
                />
              </label>

              <label style={field}>
                Total judgment entered (optional snapshot)
                <input
                  value={form.total_judgment_entered}
                  onChange={(e) => setField("total_judgment_entered", e.target.value)}
                  style={input}
                  placeholder="2592.53"
                />
              </label>

              <label style={field}>
                Status
                <select
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                  style={input}
                >
                  <option value="OPEN">OPEN</option>
                  <option value="PAID">PAID</option>
                  <option value="CLOSED">CLOSED</option>
                </select>
              </label>
            </div>

            <div style={{ fontSize: 12, color: "#666" }}>
              We won’t create anything yet — we post once at the end.
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <PartyEditor title="Plaintiffs" parties={plaintiffs} setParties={setPlaintiffs} />
            <PartyEditor title="Defendants" parties={defendants} setParties={setDefendants} />
          </div>
        )}

        {step === 5 && (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>Contact + Flags</h3>

            <div style={grid2}>
              <label style={field}>
                Status flags
                <input
                  value={form.status_flags}
                  onChange={(e) => setField("status_flags", e.target.value)}
                  style={input}
                  placeholder="NEW / URGENT / etc"
                />
              </label>

              <label style={field}>
                Follow up date
                <input
                  value={form.follow_up_date}
                  onChange={(e) => setField("follow_up_date", e.target.value)}
                  style={input}
                  placeholder="YYYY-MM-DD"
                />
              </label>

              <label style={field}>
                Letter type sent
                <input
                  value={form.letter_type_sent}
                  onChange={(e) => setField("letter_type_sent", e.target.value)}
                  style={input}
                  placeholder="Demand / Notice / etc"
                />
              </label>

              <label style={field}>
                Payment method
                <input
                  value={form.payment_method}
                  onChange={(e) => setField("payment_method", e.target.value)}
                  style={input}
                  placeholder="CHECK / ACH / CARD"
                />
              </label>

              <label style={{ ...field, gridColumn: "1 / -1" }}>
                Payment updates
                <input
                  value={form.payment_updates}
                  onChange={(e) => setField("payment_updates", e.target.value)}
                  style={input}
                  placeholder="Notes about payment status"
                />
              </label>
            </div>

            <h4 style={{ marginTop: 18, marginBottom: 10 }}>Primary contact (optional)</h4>
            <div style={grid2}>
              <label style={field}>
                First name
                <input
                  value={form.contact_first_name}
                  onChange={(e) => setField("contact_first_name", e.target.value)}
                  style={input}
                />
              </label>
              <label style={field}>
                Last name
                <input
                  value={form.contact_last_name}
                  onChange={(e) => setField("contact_last_name", e.target.value)}
                  style={input}
                />
              </label>
              <label style={field}>
                Phone
                <input
                  value={form.contact_phone}
                  onChange={(e) => setField("contact_phone", e.target.value)}
                  style={input}
                />
              </label>
              <label style={field}>
                Zip
                <input
                  value={form.contact_zip}
                  onChange={(e) => setField("contact_zip", e.target.value)}
                  style={input}
                />
              </label>
              <label style={{ ...field, gridColumn: "1 / -1" }}>
                Address
                <input
                  value={form.contact_address}
                  onChange={(e) => setField("contact_address", e.target.value)}
                  style={input}
                />
              </label>
              <label style={field}>
                City
                <input
                  value={form.contact_city}
                  onChange={(e) => setField("contact_city", e.target.value)}
                  style={input}
                />
              </label>
              <label style={field}>
                State
                <input
                  value={form.contact_state}
                  onChange={(e) => setField("contact_state", e.target.value)}
                  style={input}
                />
              </label>

              <label style={{ ...field, gridColumn: "1 / -1" }}>
                Notes
                <textarea
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                  style={{ ...input, minHeight: 90 }}
                  placeholder="Anything important for the collector"
                />
              </label>
            </div>
          </div>
        )}

        {step === 6 && (
          <div style={card}>
            <h3 style={{ marginTop: 0 }}>Review</h3>
            <div style={{ fontSize: 13, color: "#444" }}>
              <p style={{ marginTop: 0 }}>
                This will create the judgment and parties in one shot:
                <br />

                <code>POST /api/judgments/full</code>
              </p>
              <pre style={pre}>
{JSON.stringify(buildPayload(), null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18 }}>
        <button type="button" onClick={() => navigate("/judgments")} style={btnGhost}>
          Cancel
        </button>

        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" onClick={prev} disabled={step === 0 || saving} style={btn}>
            Back
          </button>

          {step < steps.length - 1 ? (
            <button type="button" onClick={next} disabled={saving} style={btnPrimary}>
              Next
            </button>
          ) : (
            <button type="button" onClick={submit} disabled={saving} style={btnPrimary}>
              {saving ? "Creating…" : "Create Judgment"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Minimal styling (matches your inline-style approach) */
const card = {
  border: "1px solid #e5e5e5",
  borderRadius: 10,
  padding: 16,
  background: "#fff",
};

const subCard = {
  border: "1px solid #eee",
  borderRadius: 10,
  padding: 12,
  background: "#fafafa",
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};

const field = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  fontSize: 13,
};

const input = {
  padding: "8px 10px",
  border: "1px solid #ddd",
  borderRadius: 8,
  outline: "none",
  fontSize: 14,
};

const btn = {
  padding: "8px 12px",
  border: "1px solid #ddd",
  borderRadius: 8,
  background: "#fafafa",
  cursor: "pointer",
};

const btnPrimary = {
  ...btn,
  border: "1px solid #1976d2",
  background: "#1976d2",
  color: "white",
};

const btnGhost = {
  ...btn,
  background: "transparent",
};

const btnDanger = {
  ...btn,
  border: "1px solid #dc3545",
  background: "#dc3545",
  color: "white",
};

const notice = {
  marginTop: 14,
  border: "1px solid",
  borderRadius: 10,
  padding: 12,
};

const progressTrack = {
  height: 8,
  background: "#eee",
  borderRadius: 999,
  overflow: "hidden",
};

const progressFill = {
  height: "100%",
  background: "#1976d2",
};

const pre = {
  background: "#0b1020",
  color: "#e7e7e7",
  padding: 12,
  borderRadius: 10,
  overflowX: "auto",
  fontSize: 12,
};
